import { useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useCan, useUser } from "@/lib/auth";
import { fmtDateTime } from "@/lib/date";
import {
  useGrantRole,
  useGrantUserPermission,
  usePermissions,
  useRevokeRole,
  useRevokeUserPermission,
  useRoles,
  useUpdateUser,
  useUsers,
  type UserRow,
} from "@/lib/rbac";

import { EffectivePermissions } from "./EffectivePermissions";

/**
 * Drawer for a user record. Mounted once inside UsersPage; visibility derives
 * from the route's search params (mirrors partners/PartnerDrawer):
 *
 *   ?u=<userId>                → quick view drawer
 *   ?u=<userId>&expanded=true  → expanded (full page)
 *   no `u`                     → closed
 */
export function UserDrawer() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/system/users" });
  const users = useUsers();

  const userId = search.u ?? null;
  const expanded = !!search.expanded;
  const open = userId != null;
  const user = users.data?.find((u) => u.id === userId) ?? null;

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v && !expanded) navigate({ to: "/system/users", search: {} });
      }}
      expanded={expanded}
      onExpandedChange={(exp) => {
        if (userId == null) return;
        navigate({
          to: "/system/users",
          search: exp ? { u: userId, expanded: true } : { u: userId },
        });
      }}
      onClose={() => {
        if (userId == null) return;
        // Peel: expanded → quick → closed.
        if (expanded) navigate({ to: "/system/users", search: { u: userId } });
        else navigate({ to: "/system/users", search: {} });
      }}
      title={user ? user.name || user.email : "User"}
      subtitle={user?.email ?? ""}
    >
      {user && <UserDetail user={user} />}
    </EntityDrawer>
  );
}

function UserDetail({ user }: { user: UserRow }) {
  const me = useUser();
  const canManageUsers = useCan("core.users.manage");
  const canManageRoles = useCan("core.roles.manage");
  const isSelf = me.id === user.id;

  return (
    <div className="flex flex-col gap-6 p-4">
      <IdentitySection
        user={user}
        isSelf={isSelf}
        canManage={canManageUsers}
      />
      <RolesSection user={user} canManage={canManageRoles} />
      <section>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Effective permissions
        </h3>
        <EffectivePermissions user={user} />
      </section>
      <DirectGrantsSection user={user} canManage={canManageRoles} />
    </div>
  );
}

// ── Identity (name, status, login metadata) ─────────────────────────────────

function IdentitySection({
  user,
  isSelf,
  canManage,
}: {
  user: UserRow;
  isSelf: boolean;
  canManage: boolean;
}) {
  const update = useUpdateUser();
  const [name, setName] = useState(user.name);

  // Re-seed the input when a different user is selected (or a save lands).
  useEffect(() => setName(user.name), [user.id, user.name]);

  const dirty = name.trim() !== user.name;
  const save = () => {
    if (!dirty || update.isPending) return;
    update.mutate({ userId: user.id, body: { name: name.trim() } });
  };

  return (
    <section className="flex flex-col gap-3">
      <div>
        <Label htmlFor="user-name" className="text-xs">
          Name
        </Label>
        <div className="mt-1 flex gap-2">
          <Input
            id="user-name"
            value={name}
            disabled={!canManage || update.isPending}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
          />
          {dirty && canManage && (
            <Button size="sm" onClick={save} disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <div className="text-xs text-muted-foreground">Last login</div>
          <div className="tabular-nums">{fmtDateTime(user.last_login)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Created</div>
          <div className="tabular-nums">{fmtDateTime(user.created_at)}</div>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
        <div className="flex items-center gap-2">
          <StatusBadge value={user.is_active ? "active" : "inactive"} />
          <span className="text-xs text-muted-foreground">
            {isSelf
              ? "You can't deactivate your own account."
              : "Inactive users keep their row but lose all access."}
          </span>
        </div>
        <Switch
          checked={user.is_active}
          disabled={!canManage || isSelf || update.isPending}
          onCheckedChange={(v) =>
            update.mutate({ userId: user.id, body: { is_active: v } })
          }
          aria-label="Active"
        />
      </div>
      {update.error && (
        <p className="text-sm text-destructive">
          {update.error instanceof Error ? update.error.message : "Update failed."}
        </p>
      )}
    </section>
  );
}

// ── Roles ───────────────────────────────────────────────────────────────────

function RolesSection({ user, canManage }: { user: UserRow; canManage: boolean }) {
  const roles = useRoles();
  const grant = useGrantRole();
  const revoke = useRevokeRole();
  const busy = grant.isPending || revoke.isPending;

  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Roles
      </h3>
      <div className="flex flex-wrap gap-2">
        {roles.data?.map((r) => {
          const has = user.roles.some((x) => x.id === r.id);
          return (
            <Button
              key={r.id}
              variant={has ? "default" : "outline"}
              size="sm"
              disabled={!canManage || busy}
              title={r.description || r.key}
              onClick={() =>
                has
                  ? revoke.mutate({ userId: user.id, roleId: r.id })
                  : grant.mutate({ userId: user.id, roleId: r.id })
              }
            >
              {has ? "✓ " : ""}
              {r.name}
            </Button>
          );
        })}
      </div>
      {!canManage && (
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          You need core.roles.manage to change role assignments.
        </p>
      )}
    </section>
  );
}

// ── Advanced: direct permission grants ──────────────────────────────────────

function DirectGrantsSection({ user, canManage }: { user: UserRow; canManage: boolean }) {
  const [openSection, setOpenSection] = useState(false);
  const [q, setQ] = useState("");
  const perms = usePermissions();
  const grant = useGrantUserPermission();
  const revoke = useRevokeUserPermission();
  const busy = grant.isPending || revoke.isPending;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = perms.data ?? [];
    if (needle) {
      out = out.filter(
        (p) => p.key.includes(needle) || p.description.toLowerCase().includes(needle),
      );
    }
    return out;
  }, [perms.data, q]);

  const Chevron = openSection ? ChevronDown : ChevronRight;

  return (
    <section>
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium hover:bg-accent"
        aria-expanded={openSection}
        onClick={() => setOpenSection((v) => !v)}
      >
        <Chevron className="h-4 w-4 text-muted-foreground" />
        Advanced: direct grants
        <span className="ml-auto rounded-md bg-neutral-light px-2 py-0.5 text-xs tabular-nums">
          {user.direct_permissions.length}
        </span>
      </button>
      {openSection && (
        <div className="mt-2 flex flex-col gap-2">
          <p className="text-xs text-muted-foreground">
            One-off permissions granted to this user specifically, outside any
            role. Prefer roles; reach for these for exceptions (e.g. a single
            agent grant or feature admin).
          </p>
          <Input
            placeholder="Filter permissions…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="grid max-h-72 grid-cols-1 gap-1 overflow-y-auto rounded-md border p-2">
            {filtered.map((p) => {
              const has = user.direct_permissions.some((x) => x.id === p.id);
              return (
                <Button
                  key={p.id}
                  variant={has ? "default" : "ghost"}
                  size="sm"
                  className="h-auto justify-start py-1 text-left"
                  disabled={!canManage || busy}
                  title={p.description}
                  onClick={() =>
                    has
                      ? revoke.mutate({ userId: user.id, permId: p.id })
                      : grant.mutate({ userId: user.id, permId: p.id })
                  }
                >
                  <span className="font-mono text-xs">
                    {has ? "✓ " : ""}
                    {p.key}
                  </span>
                </Button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-1 py-2 text-xs text-muted-foreground">No matches.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
